<script setup>
import dayjs from 'dayjs'

const props = defineProps({
  categories: {
    type: Array,
    default: () => [],
  },
  article: {
    type: Object,
    default: () => {},
  },
  date: {
    type: String,
    default: '',
  },
  slug: {
    type: String,
    default: '',
  },
  custom: {
    type: Object,
    default: () => {},
  },
  activeIndex: {
    type: Number,
    default: null,
  },
  index: {
    type: Number,
    default: null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
})

const articleImage = computed(() => {
  return props.article?.image?.filename
    || props.article?.image
    || props.article?.media?.filename
    || props.article?.media
    || props.article?.poster?.filename
    || props.article?.poster
    || props.article?.thumbnail?.filename
    || props.article?.thumbnail
    || ''
})
const articleTitle = computed(() => {
  return props.article?.title
    || props.article?.name
    || props.slug?.split('/').filter(Boolean).at(-1)?.replace(/-/g, ' ')
    || ''
})
const articleDescription = computed(() => props.article?.description || props.article?.subtitle || props.article?.excerpt || '')
const articleDate = computed(() => props.article?.date || props.date)
</script>

<template>
  <nuxt-link
    :to="localePath({ path: `/${slug}` })"
    :class="activeIndex === index ? 'shadow-custom-lg/90 scale-[101.5%]' : 'scale-100'"
    class="group/card relative block h-full min-h-0 w-full cursor-pointer overflow-hidden rounded-lg bg-primary text-left text-white shadow-lg transition-transform duration-500 ease-out will-change-transform focus-visible:scale-[101.5%] hover:scale-[101.5%] lg:rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary-red-100"
  >
    <CardSkeletonCard v-if="loading" />
    <div v-else class="relative h-full min-h-0 w-full overflow-hidden rounded-lg lg:rounded-xl">
      <nuxt-img
        v-if="articleImage"
        loading="lazy"
        width="620"
        height="300"
        :src="articleImage"
        :alt="articleTitle"
        class="absolute inset-0 h-full w-full scale-100 rounded-lg object-cover object-center transition-transform duration-700 ease-out will-change-transform group-focus-visible/card:scale-105 group-hover/card:scale-105 lg:rounded-xl"
      />
      <div v-else class="absolute inset-0 rounded-lg bg-primary/20 lg:rounded-xl" />

      <div class="absolute inset-0 rounded-lg from-primary/0 via-primary/20 to-primary/50 bg-gradient-to-b transition duration-500 lg:rounded-xl group-focus-visible/card:via-primary/50 group-hover/card:via-primary/50 group-focus-visible/card:to-primary group-hover/card:to-primary" />
      <div class="absolute inset-x-0 bottom-0 p-3 transition duration-500 ease-out sm:p-4 xl:p-4">
        <h2 v-if="articleTitle" class="line-clamp-2 max-w-[94%] translate-y-2 text-[12px] text-white font-semibold leading-[1.16] transition duration-500 ease-out sm:text-[14px] xl:text-[16px] group-focus-visible/card:-translate-y-1 group-hover/card:-translate-y-1">
          {{ articleTitle }}
        </h2>

        <div v-if="articleDate" class="mt-0 max-h-0 translate-y-1 overflow-hidden text-[12px] text-secondary-red-100 font-bold uppercase opacity-0 transition-all duration-500 ease-out group-focus-visible/card:mt-2 group-hover/card:mt-2 group-focus-visible/card:max-h-7 group-hover/card:max-h-7 group-focus-visible/card:translate-y-0 group-hover/card:translate-y-0 sm:text-[13px] xl:text-[13px] group-focus-visible/card:opacity-100 group-hover/card:opacity-100">
          {{ dayjs(articleDate).format('DD MMMM YYYY') }}
        </div>

        <!-- <div
          v-if="articleDescription"
          class="mt-0 max-h-0 overflow-hidden transition-all duration-500 delay-75 ease-out group-focus-visible/card:mt-1.5 group-hover/card:mt-1.5 group-focus-visible/card:max-h-14 group-hover/card:max-h-14"
        >
          <p class="line-clamp-2 max-w-[94%] translate-y-1 text-[14px] text-white/90 leading-[1.45] opacity-0 transition-[opacity,transform] duration-500 delay-75 ease-out group-focus-visible/card:translate-y-0 group-hover/card:translate-y-0 sm:text-[15px] xl:text-[13px] group-focus-visible/card:opacity-100 group-hover/card:opacity-100">
            {{ articleDescription }}
          </p>
        </div> -->

        <div class="mt-0 max-h-0 inline-flex translate-y-2 items-center gap-1 overflow-hidden rounded-full bg-secondary-red-100 px-2 py-1 text-[13px] text-white font-bold leading-none opacity-0 shadow-[0_7px_0_rgba(15,13,54,0.28)] ring-2 ring-secondary-red-100/20 transition-all duration-500 delay-100 ease-out group-focus-visible/card:mt-3 group-hover/card:mt-3 group-focus-visible/card:max-h-10 group-hover/card:max-h-10 group-focus-visible/card:translate-y-0 group-hover/card:translate-y-0 group-focus-visible/card:py-2 group-hover/card:py-2 sm:px-3 sm:text-[14px] xl:text-[12px] group-focus-visible/card:opacity-100 group-hover/card:opacity-100">
          <span>{{ $t('button.more') }}</span>
          <svg class="h-4 w-4 translate-x-0 transition-transform duration-300 group-focus-visible/card:translate-x-1 group-hover/card:translate-x-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path fill="currentColor" d="M9.29 15.88L13.17 12L9.29 8.12a.996.996 0 1 1 1.41-1.41l4.59 4.59c.39.39.39 1.02 0 1.41L10.7 17.3a.996.996 0 0 1-1.41 0c-.38-.39-.39-1.03 0-1.42z" />
          </svg>
        </div>
      </div>
    </div>
  </nuxt-link>
</template>
